<template>
  <q-select
    ref="select"
    hide-bottom-space
    stack-label
    :use-input="useInput"
    :disable="disable"
    :clearable="clearable"
    :error="error"
    :error-message="errorMessage"
    :dense="dense"
    :outlined="outlined"
    :borderless="borderless"
    :label="label"
    :option-value="optionValue"
    :option-label="optionLabel"
    :model-value="modelValue"
    :autofocus="autofocus"
    :loading="loading"
    :input-debounce="debounce"
    :options="results"
    :use-chips="multiple && display === 'chip'"
    :multiple="multiple"
    :placeholder="showPlaceholderText ? placeholder : undefined"
    :popup-content-class="`${separator ? 'q-list--separator' : ''}`"
    :behavior="$q.screen.width < 1024 ? 'dialog' : 'menu'"
    :class="inputClass"
    v-bind="$attrs"
    @filter="performSearch"
    @update:model-value="onUpdatePopoverVal"
    @clear="onClearVal"
    @blur="$emit('blur')"
    @focus="$emit('focus')"
    @popup-show="onPopupShow"
  >
    <template
      v-if="hideSelectedItems"
      #selected
    >
      <slot name="selected">
        <q-chip
          v-if="useInput"
          color="grey-2"
          dense
        >
          {{ selectedAmt }} selected {{ pluraliseString(friendlyName, selectedAmt) }}
        </q-chip>
        <template v-else>
          {{ selectedAmt }} selected {{ pluraliseString(friendlyName, selectedAmt) }}
        </template>
      </slot>
    </template>

    <template v-if="hasOptionSlot" #option="scope">
      <slot name="option" v-bind="scope" />
    </template>

    <template
      v-if="hasSelectedChipSlot && !hideSelected && display === 'chip'"
      #selected-item="scope"
    >
      <slot name="selected-chip" v-bind="scope" />
    </template>

    <template
      v-if="hasCreateSlot || hasAdvancedSlot"
      #before-options
    >
      <slot v-if="hasCreateSlot" name="create" />
      <slot v-if="hasAdvancedSlot" name="advanced" />
    </template>

    <template #no-option>
      <slot
        v-if="hasCreateSlot"
        name="create"
      />
      <slot
        v-if="hasAdvancedSlot"
        name="advanced"
      />
      <no-results
        align="left"
        title="No results to display"
        without-icon
      />
    </template>

    <template
      v-if="icon"
      #prepend
    >
      <q-icon :name="icon" />
    </template>

    <template
      v-if="hasAfterSlot"
      #after
    >
      <slot name="after" />
    </template>
  </q-select>

  <!-- Display selected items afterward if chip mode is disabled -->
  <div v-if="selectedItems.length > 0 && !hideSelected && display === 'item'" class="q-gutter-sm q-mt-sm">
    <q-field
      v-for="(item, index) in selectedItems"
      :key="index"
      class="without-padding"
      hide-bottom-space
      outlined
      dense
    >
      <div class="full-width">
        <slot
          name="selected-item"
          v-bind="{ item, index }"
        />
      </div>
    </q-field>
  </div>
</template>

<script>
import input from 'src/helpers/input'

/**
 * Base implementation for any modules which require a QSelect field for model searching
 * Displays a search box, filtered items within the dropdown, and the option for
 * showing selected items as a chip or underneath the dropdown
 *
 * SLOTS:
 * - selected: show X amount of selected items
 * - after: prepend content to right of input
 * - create: slot above items to show a + button
 * - advanced: slot above items (under create) to show advanced filtering items
 * - option: (scope - from QSelect) - render an option in the dropdown
 * - selected-chip: (scope - from QSelect selected-item) - render each selected item as a chip inside the input box
 * - selected-item: (item, index) - render each selected item as an item underneath the input box
 *
 * EMIT:
 * - blur: input loses focus
 * - focus - input is focused
 * - clear - model state is cleared
 * - input: (val) model state change
 * - clickedResult: (val) result which was clicked / added to the model
 * - removedResult: (val) result which was clicked (deselected) and removed from the model
 * - changed: emit X milliseconds after the input has changed (only applies to inputPopoverMixin)
 */
export default {
  mixins: [input],
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [Array, Object],
      default: () => {
        return []
      }
    },
    // Icon to render to the left of input box
    icon: {
      type: String
    },
    loading: {
      type: Boolean,
      default: false
    },
    borderless: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String
    },
    label: {
      type: String
    },
    placeholder: {
      type: String
    },
    clearable: {
      type: Boolean,
      default: false
    },
    separator: {
      type: Boolean,
      default: false
    },
    useInput: {
      type: Boolean,
      default: true
    },
    // Hide the selected items on this input
    // Will instead show X items counter
    hideSelected: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    // Allow multiple items to be selected
    multiple: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    // How long after input should the model value be updated (default 250ms - no delay)
    debounce: {
      type: Number,
      default: 250
    },
    // How to display the selected items
    // chip (inside input) or item (underneath)
    display: {
      type: String,
      default: 'chip'
    },
    optionValue: {
      type: String,
      default: 'id'
    },
    optionLabel: {
      type: String,
      required: true
    },
    // Friendly name of the input type
    // such as item, client, appointment
    friendlyName: {
      type: String,
      default: 'item'
    },
    // Function for performing a search
    // Param: val (the value to search over)
    search: {
      type: Function,
      required: true
    },
    // Options to display in list from search endpoint/function
    results: {
      type: Array,
      required: true
    },
    // On first click, hide the popup - after consecutive times multiple items can be clicked
    hideOnFirstClick: {
      type: Boolean,
      default: false
    },
    // Inject classes to the QSelect
    inputClass: {
      type: [String, Object]
    }
  },
  emits: ['blur', 'focus'],
  data () {
    return {
      hasClickedFirstResult: false
    }
  },
  computed: {
    selectedAmt () {
      return this.selectedItems.length
    },
    hasCreateSlot () {
      return !!this.$slots.create
    },
    hasAdvancedSlot () {
      return !!this.$slots.advanced
    },
    hasAfterSlot () {
      return !!this.$slots.after
    },
    hasOptionSlot () {
      return !!this.$slots.option
    },
    hasSelectedChipSlot () {
      return !!this.$slots['selected-chip']
    },
    selectedItems () {
      if (!this.multiple) {
        if (!this.modelValue) {
          return []
        }
        return [this.modelValue]
      }
      return this.modelValue || []
    },
    showPlaceholderText () {
      return this.selectedAmt < 1
    },
    hideSelectedItems () {
      return (this.hideSelected && this.selectedAmt > 0) || (this.display === 'item' && this.selectedAmt > 0)
    }
  },
  methods: {
    performSearch (val, success, abort) {
      return this.search(val, success, abort)
    },
    onPopupShow () {
      if (!this.$q.screen.lt.md) {
        return
      }
      // Open the picker with results visible but the search box unfocused, so the
      // keyboard doesn't pop up automatically. Tapping the box still lets you search.
      this.$nextTick(() => {
        const active = document.activeElement
        if (active && typeof active.blur === 'function') {
          active.blur()
        }
      })
    },
    clearInput () {
      if (!this.$refs.select) {
        return
      }
      this.$refs.select.updateInputValue('', true)
      this.hidePopup()
    },
    refresh () {
      this.$refs.select.refresh()
    },
    showPopup () {
      this.$refs.select.showPopup()
    },
    hidePopup () {
      this.$refs.select.hidePopup()
    },
    blur () {
      this.hidePopup()
      this.$nextTick(() => {
        this.$refs.select.blur()
      })
    },
    onUpdatePopoverVal (val, newModel = false) {
      const status = this.onUpdateVal(val, newModel)
      if (status !== 'click') {
        return
      }
      // Close the picker (and dismiss the soft keyboard) once a selection is made.
      // Single-select: always close. Multi-select: only when hideOnFirstClick is set.
      if (!this.multiple) {
        this.blur()
      }
      else if (this.hideOnFirstClick && !this.hasClickedFirstResult) {
        this.hasClickedFirstResult = true
        this.blur()
      }
    }
  },
  mounted () {
    if (this.autofocus) {
      this.$nextTick(() => {
        this.showPopup()
      })
    }
  }
}
</script>
